import React, { ReactElement, useEffect, useState, FC } from 'react';
import * as yup from 'yup';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Forms from '../../../components/Forms/Forms';
import { digitalCodesConstants } from '../../../constants/digitalCodesConstants';
import { postROMSizeRequestAction } from '../../../services/digitalCodesService';
import { isEmptyObject } from '../../../utils/dataUtils';
import { createMessageForError, toasterNotify } from '../../../utils/toaster';
import { validateToSchema } from '../../../utils/validationUtils';
import { Alert, Col, Row } from 'react-bootstrap';
import { useReferenceData } from '../../../hooks/reduxHooks';

const { APPROVED } = digitalCodesConstants.priceRequestStatuses;

const schema = yup.object().shape({
	rom_size_bytes: yup.number().required('ROM Size Bytes are required').positive('ROM Size Bytes must be a positive number').integer('ROM Size Bytes must be an integer'),
});

interface ROMSizeReviewModalProps {
	show?: boolean;
	onClose?: () => void;
	onChange?: () => void;
	component?: ProductComponentData;
	priceRequest?: CIBPriceRequestData;
}
const ROMSizeReviewModal: FC<ROMSizeReviewModalProps> = ({
	show,
	onClose,
	onChange,
	component,
	priceRequest,
}: ROMSizeReviewModalProps): ReactElement => {
	const platforms = useReferenceData('platforms');
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showAllErrors, setShowAllErrors] = useState<boolean>(false);

	useEffect(() => {
		if (show) {
			setShowAllErrors(false);
			setFormValues({});
		}
	}, [show]);

	const validationErrors = validateToSchema(schema, formValues);

	const getRomGiB = () => {
		const bytes = formValues?.rom_size_bytes;

		if (bytes === undefined || bytes === null || bytes === '') {
			return '';
		}

		const n = Number(bytes);
		if (!Number.isFinite(n)) {
			return '';
		}

		const gib = n / 1073741824;
		const truncated = Math.trunc(gib * 100) / 100;

		return `${truncated.toFixed(2)} GiB`;
	};

	return (
		<BaseModal
			show={show || false}
			onCancel={onClose && (() => onClose())}
			isSubmitting={isSubmitting}
		>
			<BaseModal.Title>Approve Code-in-Box Request</BaseModal.Title>
			<Alert variant="info">
				Do you wish to approve this Code-in-Box price request for {component?.game_name}?{' '}
				{priceRequest?.requested_product_type && (
					<>
						Approving this request will also set the component's product type
						with the requested product type below.
					</>
				)}
			</Alert>

			<Row className="form-group">
				<Col
					sm={4}
					className="text-sm-right pt-0 form-label col-form-label col-sm-4"
				>
					{priceRequest?.requested_product_type && (
						<span className="d-block">Requested</span>
					)}
					Product Type
				</Col>
				<Col sm={8} className="ml-sm-0 col-sm-8 my-auto">
					{
						platforms?.find(
							({ platform_code }) =>
								platform_code ===
								(priceRequest?.requested_product_type
									? priceRequest?.requested_product_type
									: component?.product_type),
						)?.platform_name
					}
				</Col>
			</Row>

			<Row className="form-group">
				<Col
					sm={4}
					className="text-sm-right pt-0 form-label col-form-label col-sm-4"
				>
					ROM Size Bytes
				</Col>
				<Col sm={8} className="col-sm-8 my-auto">
					<Forms
						values={formValues || {}}
						onChange={(newFormValues) => setFormValues(newFormValues)}
						validationErrors={validationErrors}
						showAllErrors={showAllErrors}
						vertical
					>
						<Forms.Number id="rom_size_bytes" maxLength={12} no-stepper>
							<Forms.Postscript>
								<div className="help-block">{getRomGiB()}</div>
							</Forms.Postscript>
						</Forms.Number>
					</Forms>
				</Col>
			</Row>

			<BaseModal.Submit
				onClick={async () => {
					if (isSubmitting) {
						return;
					}
					if (!isEmptyObject(validationErrors)) {
						setShowAllErrors(true);
						return;
					}

					setIsSubmitting(true);
					try {
						await postROMSizeRequestAction(String(priceRequest?.cib_price_request_id), {
							action: APPROVED,
							rom_size_bytes: formValues.rom_size_bytes,
						});
						toasterNotify(
							`Status has been updated to the code-in-box price request for "${component?.game_name}"`,
							'info',
						);
						onChange && onChange();
					} catch (error) {
						toasterNotify(
							createMessageForError(
								error as Error,
								'approving the code-in-box price request',
							),
							'error',
							error as Error,
						);
						setIsSubmitting(false);
					} finally {
						onClose && onClose();
					}
				}}
			>
				Approve
			</BaseModal.Submit>
			<BaseModal.Cancel onClick={onClose}>
				Cancel
			</BaseModal.Cancel>
		</BaseModal>
	);
};

export default ROMSizeReviewModal;
